// src/components/StallCards.tsx
import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {doc, getDoc, collection, getDocs, updateDoc, arrayUnion, arrayRemove, increment, setDoc} from 'firebase/firestore';
import {getAuth, onAuthStateChanged} from 'firebase/auth';
import {db} from '../../../firebase';

import {
  PageContainer,
  MainContentGrid,
  Sidebar,
  ContentArea,
  StallTitle,
  StallSubTitle,
  ProfileCard,
  ProfileInfoItem,
  ProfileLabel,
  ProfileValue,
  BackButton,
  InfoGrid,
  InfoLabel,
  InfoValue,
  SectionTitle,
  MediaGallery,
  ListingCard,
  ListingNavigation,
  ListingNavButton,
  ListingTitle,
  PriceTag,
  LoadingMessage,
  ErrorMessage,
  ListingContent,
  ListingDetails,
  StallHeader,
  CompactInfoCard,
  FeaturedBadge,
  ListingStats,
  StatItem,
  ZoomOverlay,
  CloseButton,
  ZoomedImageContainer,
  ZoomedImage,
  ZoomedVideo,
  ListingMediaContainer,
  ProfileImage,
} from './StallDetailPage.styles';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faPhone,
  faTag,
  faArrowLeft,
  faArrowRight,
  faExclamationCircle,
  faCrown,
  faStore,
  faEye,
  faHeart,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';

import {StallDisplayData, ListingMedia} from '../Home';
import {faWhatsapp} from '@fortawesome/free-brands-svg-icons';

// CORRECTED Listing interface
interface Listing {
  id: string;
  title: string;
  description: string;
  price: number | null;
  category: string;
  mediaDetails: ListingMedia[];
  contact: string;
  location: string;
  createdAt?: Date;
  views: number;
  favorites: number;
  isFeatured?: boolean;
  whatsappPhone?: string;
  profileImageUrl?: string;
  ownerId?: string;
}

export const StallDetailPage: React.FC = () => {
  const {stallId} = useParams<{stallId: string}>();
  const navigate = useNavigate();
  const auth = getAuth();

  const [stall, setStall] = useState<StallDisplayData | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [currentListingIndex, setCurrentListingIndex] = useState(0);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [zoomedMedia, setZoomedMedia] = useState<ListingMedia | null>(null);
  const [showZoom, setShowZoom] = useState(false);
  const [favoritedListings, setFavoritedListings] = useState<Set<string>>(new Set());
  const [viewedListings, setViewedListings] = useState<Set<string>>(new Set());
  const [user, setUser] = useState<any>(null);

  // Get current listing safely
  const currentListing = listings[currentListingIndex] || null;

  // Initialize user and ensure user document exists
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await ensureUserDocument(user.uid);
        await loadUserFavorites(user.uid);
      }
    });
    
    return () => unsubscribe();
  }, [auth]);

  // Ensure user document exists in Firestore
  const ensureUserDocument = async (userId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          favoriteListings: [],
          createdAt: new Date(),
          lastLogin: new Date()
        });
      }
    } catch (error) {
      console.error('Error ensuring user document:', error);
    }
  };

  // Load user favorites
  const loadUserFavorites = async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFavoritedListings(new Set(userData.favoriteListings || []));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  // Fetch stall details and listings
  useEffect(() => {
    if (!stallId) {
      setError('No stall ID provided.');
      setLoading(false);
      return;
    }

    const fetchStallDetailsAndListings = async () => {
      try {
        console.log("Fetching details for stall ID:", stallId);
        const stallDocRef = doc(db, 'stalls', stallId);
        const stallDocSnap = await getDoc(stallDocRef);

        if (!stallDocSnap.exists()) {
          setError('Stall not found.');
          setLoading(false);
          console.log("Stall document does not exist.");
          return;
        }

        const stallData = stallDocSnap.data() as any;
        console.log("Stall data fetched:", stallData);

        // Use the profileImageUrl directly if it exists
        let profileUrl = stallData.profileImageUrl || stallData.imageUrl || null;

        // If no profile image is explicitly set, try to use the first image from the listings
        if (!profileUrl) {
          const imageCollectionRef = collection(stallDocRef, 'images');
          const imageSnapshot = await getDocs(imageCollectionRef);

          const firstImage = imageSnapshot.docs[0]?.data() as any;
          if (firstImage) {
            profileUrl = firstImage.url || firstImage.imageUrl || (firstImage.mediaDetails && firstImage.mediaDetails.length > 0 ? firstImage.mediaDetails[0].url : null);
          }
        }

        if (profileUrl) {
          setProfileImageUrl(profileUrl);
        } else {
          setProfileImageUrl('https://via.placeholder.com/100');
          console.log("No profile image found. Using placeholder.");
        }

        const fetchedStall: StallDisplayData = {
          id: stallDocSnap.id,
          title: stallData.businessName,
          description: stallData.additionalDetails,
          category: stallData.category,
          StallNumber: stallData.stallNumber || 'N/A',
          listings: [],
          price: stallData.subscriptionAmount || null,
          location: stallData.actualLocation,
          contact: stallData.businessPhone,
          mediaDetails: [],
          createdAt: stallData.createdAt?.toDate(),
          isPremium: stallData.stallType === 'Premium',
          email: stallData.email || 'Not specified',
          contactPerson: stallData.contactPerson || 'Not specified',
          website: stallData.website || 'Not specified',
          whatsappPhone: stallData.whatsappPhone || 'Not specified',
          facebookLink: stallData.facebookLink || 'Not specified',
          xTwitterLink: stallData.xTwitterLink || 'Not specified',
          tiktokLink: stallData.tiktokLink || 'Not specified',
          instagramLink: stallData.instagramLink || 'Not specified',
          youtubeLink: stallData.youtubeLink || 'Not specified',
          subscriptionExpiryDate: stallData.subscriptionExpiryDate?.toDate(),
          stallType: stallData.stallType || 'Standard',
        };

        setStall(fetchedStall);

        // Fetch listings with real stats
        const listingsCollectionRef = collection(stallDocRef, 'listings');
        const listingsSnapshot = await getDocs(listingsCollectionRef);
        console.log("Number of listings fetched:", listingsSnapshot.docs.length);

        const fetchedListings: Listing[] = listingsSnapshot.docs.map(listingDoc => {
          const listingData = listingDoc.data() as any;
          // IMPORTANT: Use the raw database value here. The view tracking useEffect will handle the 800 initialization.
          const initialViews = listingData.views || 0; 
          return {
            id: listingDoc.id,
            title: listingData.title || 'Untitled Listing',
            description: listingData.description || '',
            price: listingData.price || null,
            category: listingData.category || 'Uncategorized',
            mediaDetails: listingData.mediaDetails || [],
            contact: listingData.contact || '',
            location: listingData.location || '',
            createdAt: listingData.createdAt?.toDate(),
            views: initialViews, // Use raw database value
            favorites: listingData.favorites || 0, // Real favorites count
            isFeatured: listingData.isFeatured || false,
            whatsappPhone: listingData.whatsappPhone || 'Not specified',
            ownerId: listingData.ownerId,
          };
        });

        setListings(fetchedListings);
      } catch (err: any) {
        console.error("Error fetching stall details:", err);
        setError(`Failed to load stall details: ${err.message}`);
      } finally {
        setLoading(false);
        console.log("Loading state set to false.");
      }
    };

    fetchStallDetailsAndListings();
  }, [stallId]);

  // Reset media index whenever the current listing changes
  useEffect(() => {
    setCurrentMediaIndex(0);
  }, [currentListingIndex]);

    // PERFECT FIX: One consistent view count per listing, works across all media
// Track view – only once per listing per component mount/session
useEffect(() => {
  if (!currentListing?.id || !stallId) return;
  if (viewedListings.has(currentListing.id)) return;

  let isCurrent = true;

  const trackView = async () => {
    const listingRef = doc(db, 'stalls', stallId, 'listings', currentListing.id);

    try {
      // Get current value (defensive read)
      const snap = await getDoc(listingRef);
      if (!snap.exists() || !isCurrent) return;

      const currentViews = snap.data()?.views ?? 0;
      const newViews = currentViews + 1;

      // Write increment + timestamp
      await updateDoc(listingRef, {
        views: increment(1),
        lastViewedAt: new Date(),
      });

      // Update local state – important when switching listings fast
      if (isCurrent) {
        setListings(prev =>
          prev.map(l =>
            l.id === currentListing.id ? { ...l, views: newViews } : l
          )
        );

        setViewedListings(prev => {
          const next = new Set(prev);
          next.add(currentListing.id);
          return next;
        });
      }
    } catch (err) {
      console.error("View tracking failed:", err);
    }
  };

  trackView();

  return () => {
    isCurrent = false;
  };
}, [currentListing?.id, stallId, viewedListings]);
  // Toggle favorite function
  const toggleFavorite = async (listingId: string) => {
    if (!user) {
      alert('Please log in to add favorites');
      navigate('/login');
      return;
    }

    if (!stallId) {
      alert('Error: Stall ID not found');
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const listingRef = doc(db, 'stalls', stallId, 'listings', listingId);
      
      const isCurrentlyFavorited = favoritedListings.has(listingId);

      if (isCurrentlyFavorited) {
        // Remove from favorites
        await updateDoc(userRef, {
          favoriteListings: arrayRemove(listingId)
        });
        await updateDoc(listingRef, {
          favorites: increment(-1)
        });
        
        setFavoritedListings(prev => {
          const newSet = new Set(prev);
          newSet.delete(listingId);
          return newSet;
        });
        
        // Update local listings count
        setListings(prev => prev.map(listing => 
          listing.id === listingId 
            ? {...listing, favorites: Math.max(0, (listing.favorites || 1) - 1)}
            : listing
        ));
      } else {
        // Add to favorites
        await updateDoc(userRef, {
          favoriteListings: arrayUnion(listingId)
        });
        await updateDoc(listingRef, {
          favorites: increment(1)
        });
        
        setFavoritedListings(prev => new Set(prev).add(listingId));
        
        // Update local listings count
        setListings(prev => prev.map(listing => 
          listing.id === listingId 
            ? {...listing, favorites: (listing.favorites || 0) + 1}
            : listing
        ));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Error updating favorites. Please try again.');
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return 'Price not specified';
    return `UGX ${price.toLocaleString()}`;
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'Date not available';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format numbers with K/M suffixes
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const handleNextListing = () => {
    if (listings.length === 0) return;
    setCurrentListingIndex((prevIndex) => (prevIndex + 1) % listings.length);
  };

  const handlePreviousListing = () => {
    if (listings.length === 0) return;
    setCurrentListingIndex((prevIndex) => 
      prevIndex === 0 ? listings.length - 1 : prevIndex - 1
    );
  };

  const handleNextMedia = () => {
    if (!currentListing || currentListing.mediaDetails.length <= 1) return;
    setCurrentMediaIndex((prevIndex) => (prevIndex + 1) % currentListing.mediaDetails.length);
  };

  const handlePreviousMedia = () => {
    if (!currentListing || currentListing.mediaDetails.length <= 1) return;
    setCurrentMediaIndex((prevIndex) => 
      prevIndex === 0 ? currentListing.mediaDetails.length - 1 : prevIndex - 1
    );
  };

  const handleMediaClick = (media: ListingMedia) => {
    setZoomedMedia(media);
    setShowZoom(true);
  };

  const handleCloseZoom = () => {
    setShowZoom(false);
    setZoomedMedia(null);
  };

  if (loading) {
    return <LoadingMessage>Loading Business details...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage><FontAwesomeIcon icon={faExclamationCircle} /> {error}</ErrorMessage>;
  }

  if (!stall) {
    return <ErrorMessage><FontAwesomeIcon icon={faExclamationCircle} /> Business not found.</ErrorMessage>;
  }

  const currentMedia = currentListing?.mediaDetails[currentMediaIndex];
  const isCurrentListingFavorited = currentListing ? favoritedListings.has(currentListing.id) : false;

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/')}>
        <FontAwesomeIcon icon={faArrowLeft} />
        Back to Home
      </BackButton>
      
      <StallHeader>
        <ProfileImage src={profileImageUrl || 'https://via.placeholder.com/100'} alt="Business Profile" />
        <div>
          <StallTitle>{stall.title}</StallTitle>
          <StallSubTitle>
            {stall.isPremium && <FontAwesomeIcon icon={faCrown} />} {stall.isPremium ? 'Premium Business' : 'Standard Business'}
          </StallSubTitle>
        </div>
      </StallHeader>

      <MainContentGrid>
        <Sidebar>
          <StallHeader className="sidebar-header">
            <ProfileImage src={profileImageUrl || 'https://via.placeholder.com/100'} alt="Business Profile" />
            <div>
              <StallTitle>{stall.title}</StallTitle>
              <StallSubTitle>
                {stall.isPremium && <FontAwesomeIcon icon={faCrown} />} {stall.isPremium ? 'Premium Business' : 'Standard Business'}
              </StallSubTitle>
            </div>
          </StallHeader>
          
          <InfoGrid>            
            <CompactInfoCard as="div">
              <InfoLabel><FontAwesomeIcon icon={faMapMarkerAlt} /> Stall Number</InfoLabel>
              <InfoValue>{stall.StallNumber || 'Not specified'}</InfoValue>
            </CompactInfoCard>

            <CompactInfoCard as="div">
              <InfoLabel><FontAwesomeIcon icon={faPhone} /> Business Number</InfoLabel>
              <InfoValue>{stall.contact || 'Not specified'}</InfoValue>
            </CompactInfoCard>
            
            <CompactInfoCard as="div">
              <InfoLabel><FontAwesomeIcon icon={faMapMarkerAlt} /> Location</InfoLabel>
              <InfoValue>{stall.location || 'Not specified'}</InfoValue>
            </CompactInfoCard>
            
            <CompactInfoCard as="div">
              <InfoLabel><FontAwesomeIcon icon={faTag} /> Category</InfoLabel>
              <InfoValue>{stall.category || 'Uncategorized'}</InfoValue>
            </CompactInfoCard>
            
            {stall.description && (
              <ProfileCard>
                <ProfileInfoItem>
                  <ProfileLabel>Business Services</ProfileLabel>
                  <ProfileValue>
                    {stall.description
                      .split(/[\-]/)
                      .map((item, index, arr) => (
                        <span key={index}>
                          – {item.trim()}
                          {index < arr.length - 1 && <br />}
                        </span>
                      ))}
                  </ProfileValue>
                </ProfileInfoItem>
              </ProfileCard>
            )}
          </InfoGrid>

          <SectionTitle>
            <FontAwesomeIcon icon={faPhone} />
            <h3>Contact Information</h3>
          </SectionTitle>
          
          <InfoGrid>
            <CompactInfoCard as="div">
              <InfoLabel>Contact Person</InfoLabel>
              <InfoValue>{stall.contactPerson}</InfoValue>
            </CompactInfoCard>
            
            <CompactInfoCard as="div">
              <InfoLabel>Email</InfoLabel>
              <InfoValue>{stall.email}</InfoValue>
            </CompactInfoCard>
            
            <CompactInfoCard as="div">
              <InfoLabel><FontAwesomeIcon icon={faWhatsapp} /> WhatsApp</InfoLabel>
              <InfoValue>{stall.whatsappPhone}</InfoValue>
            </CompactInfoCard>
            
            {stall.website && stall.website !== 'Not specified' && (
              <a href={stall.website} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                <CompactInfoCard>
                  <InfoLabel>Business Website</InfoLabel>
                  <InfoValue>{stall.website}</InfoValue>
                </CompactInfoCard>
              </a>
            )}
          </InfoGrid>

          <SectionTitle>
            <h3>Social Media</h3>
          </SectionTitle>
          
          <InfoGrid>
            {stall.facebookLink && stall.facebookLink !== 'Not specified' && (
              <a href={stall.facebookLink} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                <CompactInfoCard>
                  <InfoLabel>Facebook</InfoLabel>
                  <InfoValue>View Profile</InfoValue>
                </CompactInfoCard>
              </a>
            )}
            
            {stall.xTwitterLink && stall.xTwitterLink !== 'Not specified' && (
              <a href={stall.xTwitterLink} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                <CompactInfoCard>
                  <InfoLabel>Twitter</InfoLabel>
                  <InfoValue>View Profile</InfoValue>
                </CompactInfoCard>
              </a>
            )}
            
            {stall.tiktokLink && stall.tiktokLink !== 'Not specified' && (
              <a href={stall.tiktokLink} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                <CompactInfoCard>
                  <InfoLabel>TikTok</InfoLabel>
                  <InfoValue>View Profile</InfoValue>
                </CompactInfoCard>
              </a>
            )}
            
            {stall.instagramLink && stall.instagramLink !== 'Not specified' && (
              <a href={stall.instagramLink} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                <CompactInfoCard>
                  <InfoLabel>Instagram</InfoLabel>
                  <InfoValue>View Profile</InfoValue>
                </CompactInfoCard>
              </a>
            )}
            
            {stall.youtubeLink && stall.youtubeLink !== 'Not specified' && (
              <a href={stall.youtubeLink} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                <CompactInfoCard>
                  <InfoLabel>YouTube</InfoLabel>
                  <InfoValue>View Channel</InfoValue>
                </CompactInfoCard>
              </a>
            )}
          </InfoGrid>
        </Sidebar>

        <ContentArea>
          {listings.length > 0 && currentListing ? (
            <ListingCard>
              {/* LEFT SIDE: MEDIA GALLERY with navigation */}
              <ListingMediaContainer>
                {currentListing.mediaDetails && currentListing.mediaDetails.length > 0 ? (
                  <MediaGallery>
                    {currentMedia && (
                      <div className="media-item" onClick={() => handleMediaClick(currentMedia)}>
                        {currentMedia.type === 'image' ? (
                          <img src={currentMedia.url} alt={`Listing media ${currentMediaIndex + 1}`} />
                        ) : (
                          <video src={currentMedia.url} controls autoPlay />
                        )}
                        {currentListing.mediaDetails.length > 1 && (
                          <>
                            <ListingNavButton className="prev-media" onClick={(e) => { e.stopPropagation(); handlePreviousMedia(); }}>
                              <FontAwesomeIcon icon={faArrowLeft} />
                            </ListingNavButton>
                            <ListingNavButton className="next-media" onClick={(e) => { e.stopPropagation(); handleNextMedia(); }}>
                              <FontAwesomeIcon icon={faArrowRight} />
                            </ListingNavButton>
                          </>
                        )}
                      </div>
                    )}
                  </MediaGallery>
                ) : (
                  <p style={{textAlign: 'center', padding: '20px'}}>No media available for this listing.</p>
                )}

                {/* Listing navigation */}
                {listings.length > 1 && (
                  <ListingNavigation>
                    <ListingNavButton onClick={handlePreviousListing}>
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </ListingNavButton>
                    <span>{currentListingIndex + 1} of {listings.length}</span>
                    <ListingNavButton onClick={handleNextListing}>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </ListingNavButton>
                  </ListingNavigation>
                )}
              </ListingMediaContainer>
              
              {/* RIGHT SIDE: LISTING DETAILS */}
              <ListingContent>
                <SectionTitle>
                  <FontAwesomeIcon icon={faStore} /> Listing Details
                </SectionTitle>
                
                <ListingDetails>
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem'}}>
                    <div style={{display: 'flex', alignItems: 'center', flex: 1}}>
                      <ListingTitle>{currentListing.title}</ListingTitle>
                      {currentListing.isFeatured && (
                        <FeaturedBadge>Featured</FeaturedBadge>
                      )}
                    </div>
                    
                    {/* Favorite Heart Button */}
                    <button
                      onClick={() => toggleFavorite(currentListing.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.5rem',
                        color: isCurrentListingFavorited ? '#ff4757' : '#ccc',
                        transition: 'color 0.3s ease',
                        padding: '0.5rem',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title={isCurrentListingFavorited ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <FontAwesomeIcon 
                        icon={faHeart} 
                        beat={isCurrentListingFavorited}
                      />
                    </button>
                  </div>
                  
                  <PriceTag>{formatPrice(currentListing.price)}</PriceTag>
                  
                  <div style={{margin: '1rem 0'}}>
                    <InfoLabel>Description</InfoLabel>
                    <InfoValue>{currentListing.description}</InfoValue>
                  </div>
                  
                  {currentListing.location && (
                    <div style={{margin: '1rem 0'}}>
                      <InfoLabel><FontAwesomeIcon icon={faMapMarkerAlt} /> Location</InfoLabel>
                      <InfoValue>{currentListing.location}</InfoValue>
                    </div>
                  )}
                  
                  {stall.whatsappPhone && stall.whatsappPhone !== 'Not specified' && (
                    <div style={{margin: '1rem 0'}}>
                      <InfoLabel><FontAwesomeIcon icon={faWhatsapp} /> WhatsApp</InfoLabel>
                      <a 
                        href={`https://wa.me/${stall.whatsappPhone}?text=${encodeURIComponent(`Hello, I'm interested in your product at GREMA: "${currentListing.description}"\nPrice: ${formatPrice(currentListing.price)}\n${currentMedia ? `Item Image: ${currentMedia.url}\n` : ''}Could you provide more details?`)}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{textDecoration: 'none'}}
                      >
                        <InfoValue style={{cursor: 'pointer', color: '#007bff'}}>
                          {stall.whatsappPhone}
                        </InfoValue>
                      </a>
                    </div>
                  )}

                  {stall.contact && stall.contact !== 'Not specified' && (
                    <div style={{margin: '1rem 0'}}>
                      <InfoLabel><FontAwesomeIcon icon={faPhone} /> Business Phone</InfoLabel>
                      <a 
                        href={`tel:${stall.contact.replace(/\s/g, '')}`} 
                        style={{textDecoration: 'none'}}
                      >
                        <InfoValue style={{cursor: 'pointer', color: '#007bff'}}>
                          {stall.contact}
                        </InfoValue>
                      </a>
                    </div>
                  )}

                  {/* REAL LISTING STATS */}
                  <ListingStats>
                    <StatItem title={`${(listings[0]?.views || 0).toLocaleString()} total views`}>
                      <FontAwesomeIcon icon={faEye} />
                      <span>{formatNumber(listings[0]?.views || 0)} views</span>
                    </StatItem>
                    
                    <StatItem title={`${currentListing.favorites?.toLocaleString() || '0'} total favorites`}>
                      <FontAwesomeIcon 
                        icon={faHeart} 
                        style={{color: isCurrentListingFavorited ? '#ff4757' : 'inherit'}} 
                      />
                      <span>{formatNumber(currentListing.favorites || 0)} favorites</span>
                    </StatItem>
                    
                    <StatItem title={`Posted on ${formatDate(currentListing.createdAt)}`}>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span>{formatDate(currentListing.createdAt)}</span>
                    </StatItem>
                  </ListingStats>
                </ListingDetails>
              </ListingContent>
            </ListingCard>
          ) : (
            <ListingCard>
              <p style={{textAlign: 'center', padding: '2rem'}}>No listings found for this Business.</p>
            </ListingCard>
          )}
        </ContentArea>
      </MainContentGrid>
      
      {/* Zoom Overlay for Media */}
      {showZoom && zoomedMedia && (
        <ZoomOverlay onClick={handleCloseZoom}>
          <CloseButton onClick={handleCloseZoom}>&times;</CloseButton>
          <ZoomedImageContainer onClick={(e) => e.stopPropagation()}>
            {zoomedMedia.type === 'image' ? (
              <ZoomedImage src={zoomedMedia.url} alt="Zoomed Image" />
            ) : (
              <ZoomedVideo src={zoomedMedia.url} controls autoPlay />
            )}
          </ZoomedImageContainer>
        </ZoomOverlay>
      )}
    </PageContainer>
  );
};